import { t } from 'i18n';
import { FC } from 'react';
import { customMediaQuery } from 'students/views/shared/styled';
import styled, { keyframes } from 'styled-components';

import aiBadge from '../assets/ai_badge.png';

interface IAITypingIndicator {
  botName?: string | null;
  botAvatar?: string | null;
  className?: string;
}

const AITypingIndicator: FC<IAITypingIndicator> = ({ botName, botAvatar, className }) => {
  return (
    <SWrapper
      className={className}
      role="status"
      aria-live="polite"
      aria-label={`${botName || 'AI'} is typing a response`}
    >
      <SMessage>
        <STypingDots aria-hidden="true">
          <SDot $delay={0} />
          <SDot $delay={0.2} />
          <SDot $delay={0.4} />
        </STypingDots>
        <SBadge src={aiBadge} alt="" aria-hidden="true" />
      </SMessage>

      <SFooter>
        {botAvatar ? <SAvatar src={botAvatar} alt="" /> : null}
        <STypingText>
          <span className="ln-sr-only">
            {t('frontend.member.assignments.ai_generating_response', {
              defaultValue: 'AI is generating a response'
            })}
          </span>
          <span aria-hidden="true">
            {botName || 'AI'}{' '}
            {t('frontend.member.assignments.is_typing', {
              defaultValue: 'is typing...'
            })}
          </span>
        </STypingText>
      </SFooter>
    </SWrapper>
  );
};

export default AITypingIndicator;

const SWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-inline-start: 44px;
  margin-block-end: 20px;
  position: relative;

  ${customMediaQuery('tablet')} {
    margin-inline-start: 62px;
  }
`;

const SMessage = styled.div`
  background: #4e35e814;
  border-radius: 10px;
  position: relative;
  padding: 20px 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  margin-block-end: 4px;

  ${customMediaQuery('tablet')} {
    padding: 24px 32px;
    margin-block-end: 8px;
  }
`;

const bounce = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-8px);
  }
`;

const STypingDots = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SDot = styled.span<{ $delay: number }>`
  width: 10px;
  height: 10px;
  background-color: #4e35e8;
  border-radius: 50%;
  animation: ${bounce} 1.4s infinite ease-in-out;
  animation-delay: ${({ $delay }) => $delay}s;
`;

const SBadge = styled.img`
  --badge-size: 36px;
  position: absolute;
  top: calc(0px - var(--badge-size) / 4);
  right: calc(0px - var(--badge-size) / 4);
  width: var(--badge-size);
  height: var(--badge-size);
`;

const SFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-inline-start: -40px;

  ${customMediaQuery('tablet')} {
    margin-inline-start: -56px;
  }
`;

const SAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;

  ${customMediaQuery('tablet')} {
    width: 48px;
    height: 48px;
  }
`;

const STypingText = styled.span`
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1rem;
  color: var(--sub-text-color);
  font-style: italic;

  ${customMediaQuery('tablet')} {
    font-size: 0.875rem;
  }
`;
